import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { store } from '../../../lib/store'
import { polygonDeleteRequest } from '../../../action/polygon-actions'
import { openShapePopup } from '../buildShapePopupContent'
import { normalizeTrackWidth } from '../../../lib/trackColor'

const SRC = 'bm-polygons-src'
const FILL = 'bm-polygons-fill'
const LINE = 'bm-polygons-line'

function buildFC(polygons) {
  const features = []
  for (const polygon of polygons) {
    let geoJson; try { geoJson = polygon.geoJson && JSON.parse(polygon.geoJson) } catch { continue }
    if (!geoJson) continue
    const feat = geoJson.features?.[0]
    if (!feat) continue
    const props = feat.properties?.line ?? {}
    features.push({
      ...feat,
      properties: {
        ...feat.properties,
        _id: polygon.id,
        _strokeColor: props.color ?? '#EA4335',
        _fillColor: props.fill ?? props.color ?? '#EA4335',
        _width: normalizeTrackWidth(props.width),
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

function teardown(m) {
  if (!m) return
  if (m.getLayer(FILL)) m.removeLayer(FILL)
  if (m.getLayer(LINE)) m.removeLayer(LINE)
  if (m.getSource(SRC)) m.removeSource(SRC)
}

const RenderPolygons3D = ({ map, polygons }) => {
  const activeMapRef = useRef(null)

  useEffect(() => {
    if (!map) return

    if (activeMapRef.current && activeMapRef.current !== map) {
      teardown(activeMapRef.current)
    }
    activeMapRef.current = map

    // Extracted so it can run both now and again on style.load. Shapes used to be
    // redrawn only by the styleKey remount, which waits for idle — i.e. for every
    // tile to settle — so a slow layer server delayed them by seconds.
    const apply = () => {
      const fc = buildFC(polygons)

      if (map.getSource(SRC)) {
        map.getSource(SRC).setData(fc)
        return
      }

      map.addSource(SRC, { type: 'geojson', data: fc })
      map.addLayer({
        id: FILL,
        type: 'fill',
        source: SRC,
        paint: { 'fill-color': ['get', '_fillColor'], 'fill-opacity': 0.3 },
      })
      map.addLayer({
        id: LINE,
        type: 'line',
        source: SRC,
        paint: { 'line-color': ['get', '_strokeColor'], 'line-width': ['get', '_width'], 'line-opacity': 0.85 },
      })

      let lastClickTime = 0
      const clickHandler = (e) => {
        const now = Date.now()
        if (now - lastClickTime < 100) return
        lastClickTime = now
        const feat = e.features?.[0]
        if (!feat) return
        const id = feat.properties._id
        const polygon = store.getState().polygons.find(p => p.id === id)
        if (!polygon) return
        let popup
        const onDelete = () => {
          popup?.remove()
          store.dispatch(polygonDeleteRequest(id))
        }
        popup = openShapePopup(map, polygon, 'poly', e.lngLat, onDelete)
      }
      const enterHandler = () => { map.getCanvas().style.cursor = 'pointer' }
      const leaveHandler = () => { map.getCanvas().style.cursor = '' }

      map.on('click', FILL, clickHandler)
      map.on('click', LINE, clickHandler)
      map.on('mouseenter', FILL, enterHandler)
      map.on('mouseenter', LINE, enterHandler)
      map.on('mouseleave', FILL, leaveHandler)
      map.on('mouseleave', LINE, leaveHandler)
    }

    apply()
    // Re-add on style.load, which fires as soon as the new style is parsed —
    // addSource only needs that, not the fully-loaded style isStyleLoaded() waits
    // for. The styleKey remount still happens later at idle; apply is idempotent
    // (setData when the source already exists) so running twice is harmless.
    map.on('style.load', apply)
    return () => map.off('style.load', apply)
  }, [map, polygons])

  useEffect(() => {
    return () => teardown(activeMapRef.current)
  }, [])

  return null
}

const mapStateToProps = state => ({ polygons: state.polygons })
export default connect(mapStateToProps)(RenderPolygons3D)
